// NuestrosClientes.tsx — Co Marca — ruta: /nuestros-clientes
import React, { useEffect, useState } from 'react';
import { ChevronRight, Star, Quote, ArrowRight, Building2, Users, TrendingUp, Award } from 'lucide-react';
import { fetchEmpresasConfianzaPublic, EmpresaConfianzaPublic } from '../../services/landing/empresaConfianzaPublicService';
import BorderGlow from '../../components/common/BorderGlow';
import { useTheme } from '../../context/ThemeContext';

const colors = {
    primary: '#c1348b',
    primaryDark: '#8f1f64',
    secondary: '#61257b',
    secondaryLight: '#8b3fa8',
    accent: '#ffd97c',
    accentDark: '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
* { box-sizing: border-box; margin: 0; padding: 0; }

@keyframes blobFloat {
  0%,100% { border-radius:60% 40% 30% 70%/60% 30% 70% 40%; }
  50%      { border-radius:30% 60% 70% 40%/50% 60% 30% 60%; }
}
@keyframes sectionFadeUp {
  from { opacity:0; transform:translateY(22px); }
  to   { opacity:1; transform:translateY(0); }
}
@keyframes pulseD {
  0%,100% { box-shadow:0 0 0 0 rgba(193, 52, 139,.35); }
  50%      { box-shadow:0 0 0 6px rgba(193, 52, 139,0); }
}

.nc-blob { animation: blobFloat 9s ease-in-out infinite; }
.nc-section { animation: sectionFadeUp .7s cubic-bezier(.16,1,.3,1) both; }
.nc-client-card {
  transition: all .25s cubic-bezier(.16,1,.3,1);
  cursor: default;
}
.nc-client-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 16px 40px var(--hover-shadow) !important;
  border-color: var(--hover-border) !important;
}
.nc-testimonial-card {
  transition: all .25s cubic-bezier(.16,1,.3,1);
}
.nc-testimonial-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(0,0,0,.08) !important;
}

@media (max-width:900px) {
  .nc-hero-layout { flex-direction: column !important; }
  .nc-hero-visual  { display: none !important; }
  .nc-clients-grid { grid-template-columns: repeat(2,1fr) !important; }
  .nc-testimonials-grid { grid-template-columns: 1fr !important; }
  .nc-stats-grid { grid-template-columns: repeat(2,1fr) !important; }
}
@media (max-width:560px) {
  .nc-clients-grid { grid-template-columns: 1fr 1fr !important; }
  .nc-stats-grid   { grid-template-columns: 1fr 1fr !important; }
}
`;

// Datos estáticos (testimonios, stats, secciones que no vienen de la API)
const TESTIMONIALS = [
    {
        name: 'Rodrigo Villanueva',
        role: 'Director de Innovación',
        company: 'Entel Bolivia',
        text: 'Co Marca transformó la forma en que trabajamos. Tener un espacio que respira innovación y está equipado con tecnología de punta nos permitió lanzar tres productos internos en tiempo récord.',
        avatar: 'RV', color: colors.primary, stars: 5,
    },
    {
        name: 'Carla Medina',
        role: 'CEO',
        company: 'Gravity Labs',
        text: 'Llevamos 2 años en Co Marca y no cambiaríamos nada. La combinación de oficina privada para el equipo core y escritorios flex para colaboradores externos es exactamente lo que necesitábamos.',
        avatar: 'CM', color: colors.secondary, stars: 5,
    },
    {
        name: 'Ignacio Torrez',
        role: 'Coordinador Nacional',
        company: 'Fundación JALA',
        text: 'Los espacios son impecables y la comunidad que se forma aquí es un valor agregado enorme. Hemos conectado con socios estratégicos simplemente por compartir el espacio.',
        avatar: 'IT', color: colors.accentDark, stars: 5,
    },
    {
        name: 'Sofía Quispe',
        role: 'Gerente Comercial',
        company: 'BNB Seguros',
        text: 'Las salas de reuniones son perfectas para nuestras presentaciones con clientes. Transmiten profesionalismo y la logística de reserva es muy sencilla.',
        avatar: 'SQ', color: colors.secondaryLight, stars: 5,
    },
    {
        name: 'Marcos Alvarado',
        role: 'Fundador',
        company: 'Nexos Bolivia',
        text: 'Tres años en Co Marca y cada renovación es una decisión fácil. El equipo siempre está disponible, el ambiente es inmejorable y la red de contactos que construís aquí no tiene precio.',
        avatar: 'MA', color: colors.primaryDark, stars: 5,
    },
    {
        name: 'Laura Bustamante',
        role: 'Directora Académica',
        company: 'INFOCAL',
        text: 'Organizamos nuestros talleres de formación aquí y la respuesta de los participantes siempre es positiva. Infraestructura de primer nivel y atención impecable.',
        avatar: 'LB', color: colors.accentDark, stars: 5,
    },
];

const STATS = [
    { value: '300+', label: 'Empresas atendidas', icon: <Building2 size={22} />, color: colors.primary },
    { value: '1,200+', label: 'Profesionales activos', icon: <Users size={22} />, color: colors.secondary },
    { value: '95%', label: 'Tasa de renovación', icon: <TrendingUp size={22} />, color: colors.accentDark },
    { value: '5★', label: 'Valoración promedio', icon: <Award size={22} />, color: colors.secondaryLight },
];

const NuestrosClientes: React.FC = () => {
    const { isDark } = useTheme();
    const [heroVisible, setHeroVisible] = useState(false);
    const [empresas, setEmpresas] = useState<EmpresaConfianzaPublic[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Cargar empresas desde la API pública
    const loadEmpresas = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchEmpresasConfianzaPublic();
            setEmpresas(data);
        } catch (err) {
            console.error('Error cargando empresas:', err);
            setError('No pudimos cargar el listado de empresas. Intenta nuevamente más tarde.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadEmpresas();
        const t = setTimeout(() => setHeroVisible(true), 80);
        return () => clearTimeout(t);
    }, []);

    // Paleta de colores cíclica para mantener la variedad visual
    const colorPalette = [colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight];
    const getColorForIndex = (idx: number) => colorPalette[idx % colorPalette.length];

    // Transformar datos de la API al formato visual que espera el diseño
    const clientesVisuales = empresas.map((emp, idx) => ({
        name: emp.nombre,
        initial: emp.nombre.slice(0, 2).toUpperCase(),
        category: emp.rubro,
        desc: emp.descripcion,
        color: getColorForIndex(idx),
        logo: emp.logo,
    }));

    // Para el hero usamos solo las primeras 9 empresas
    const heroClients = clientesVisuales.slice(0, 9);

    // Estado de carga o error en la sección de empresas
    const renderClientesGrid = () => {
        if (loading) {
            return (
                <div className="nc-clients-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '16px' }}>
                    {[...Array(8)].map((_, i) => (
                        <div key={i} style={{ background: '#f8fafc', borderRadius: '20px', padding: '24px 18px', height: '180px', animation: 'pulse 1.5s infinite' }} />
                    ))}
                </div>
            );
        }
        if (error) {
            return (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <p style={{ color: '#ef4444', marginBottom: '16px' }}>{error}</p>
                    <button
                        onClick={loadEmpresas}
                        style={{ background: colors.primary, color: '#fff', border: 'none', borderRadius: '12px', padding: '8px 20px', cursor: 'pointer' }}
                    >
                        Reintentar
                    </button>
                </div>
            );
        }
        if (clientesVisuales.length === 0) {
            return (
                <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
                    No hay empresas registradas aún.
                </div>
            );
        }
        return (
            <div className="nc-clients-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '16px' }}>
                {clientesVisuales.map((c, idx) => (
                    <div key={c.name} className="nc-client-card" style={{
                        '--hover-shadow': `${c.color}16`,
                        '--hover-border': `${c.color}35`,
                        background: '#fff', borderRadius: '20px', padding: '24px 18px',
                        border: '1.5px solid #f1f5f9',
                        boxShadow: '0 2px 12px rgba(0,0,0,.04)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
                    } as React.CSSProperties}>
                        <div style={{
                            width: '52px',
                            height: '52px',
                            borderRadius: '16px',
                            background: `linear-gradient(135deg, ${c.color}22, ${c.color}10)`,
                            border: `2px solid ${c.color}20`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                        }}>
                            {c.logo ? (
                                <img
                                    src={c.logo}
                                    alt={c.name}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            ) : (
                                <span style={{ fontWeight: '800', fontSize: '.95rem', color: c.color }}>
                                    {c.initial}
                                </span>
                            )}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '.88rem', fontWeight: '700', color: '#0f172a', marginBottom: '3px' }}>{c.name}</div>
                            <div style={{ fontSize: '.68rem', color: c.color, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: '8px' }}>{c.category}</div>
                            <div style={{ fontSize: '.75rem', color: '#64748b', lineHeight: 1.55 }}>{c.desc}</div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div style={{
            fontFamily: "'DM Sans',sans-serif",
            background: isDark ? '#0b0614' : '#fff',
            color: isDark ? '#f8fafc' : '#0f172a',
            overflowX: 'clip',
            transition: 'background-color 0.3s ease',
        }}>
            <style>{css}</style>

            {/* ══ HERO ══ */}
            <section style={{
                minHeight: '82vh',
                background: 'linear-gradient(135deg, #fff 0%, #fff8f9 55%, #fce8ee 100%)',
                display: 'flex', alignItems: 'center',
                position: 'relative', overflow: 'hidden',
                paddingTop: 'clamp(92px, 12vw, 115px)',
            }}>
                <div className="nc-blob" style={{
                    position: 'absolute', top: '-100px', right: '-100px',
                    width: '520px', height: '520px',
                    background: `linear-gradient(135deg, ${colors.primary}20, ${colors.secondary}14)`,
                    pointerEvents: 'none',
                }} />
                <div style={{ position: 'absolute', bottom: '10%', left: '4%', opacity: .09 }}>
                    {[...Array(5)].map((_, r) => (
                        <div key={r} style={{ display: 'flex', gap: '14px', marginBottom: '14px' }}>
                            {[...Array(5)].map((_, c) => (
                                <div key={c} style={{ width: '5px', height: '5px', borderRadius: '50%', background: colors.primaryDark }} />
                            ))}
                        </div>
                    ))}
                </div>

                <div style={{ maxWidth: '1180px', margin: '0 auto', padding: '0 clamp(20px,4vw,40px)', width: '100%' }}>
                    <div className="nc-hero-layout" style={{ display: 'flex', alignItems: 'center', gap: '64px' }}>
                        {/* LEFT */}
                        <div style={{
                            flex: 1, maxWidth: '580px',
                            opacity: heroVisible ? 1 : 0,
                            transform: heroVisible ? 'translateY(0)' : 'translateY(30px)',
                            transition: 'opacity .9s ease, transform .9s cubic-bezier(.16,1,.3,1)',
                        }}>
                            <nav style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '.72rem', fontWeight: '600', color: '#94a3b8', marginBottom: '18px' }}>
                                <a href="/" style={{ color: '#94a3b8', textDecoration: 'none' }}
                                    onMouseEnter={e => e.currentTarget.style.color = colors.primary}
                                    onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}>Inicio</a>
                                <ChevronRight size={10} color="#d1d5db" />
                                <span style={{ color: colors.primary, fontWeight: '700' }}>Nuestros Clientes</span>
                            </nav>
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                background: `${colors.primary}12`, border: `1.5px solid ${colors.primary}28`,
                                borderRadius: '100px', padding: '6px 16px', marginBottom: '20px',
                                animation: 'pulseD 2.5s infinite',
                            }}>
                                <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: colors.primary, display: 'inline-block' }} />
                                <span style={{ fontSize: '.76rem', fontWeight: '700', color: colors.primary, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                                    Co Marca Group
                                </span>
                            </div>
                            <h1 style={{
                                fontSize: 'clamp(2.4rem,5vw,3.6rem)', fontWeight: '900',
                                color: '#0f172a', lineHeight: 1.05, letterSpacing: '-.025em', marginBottom: '20px',
                            }}>
                                Las empresas que{' '}
                                <span style={{ color: colors.primary }}>confían en nosotros</span>
                            </h1>
                            <p style={{ fontSize: '1.05rem', color: '#475569', lineHeight: 1.85, marginBottom: '32px', maxWidth: '500px' }}>
                                Más de 300 empresas y 1,200 profesionales han elegido Co Marca como su base de operaciones en La Paz. Desde startups hasta corporaciones, construimos juntos el ecosistema de negocios más dinámico de Bolivia.
                            </p>
                            <div style={{ display: 'flex', gap: '20px', marginBottom: '32px', flexWrap: 'wrap' }}>
                                {STATS.slice(0, 3).map((s, i) => (
                                    <React.Fragment key={s.label}>
                                        <div>
                                            <div style={{ fontSize: '1.6rem', fontWeight: '900', color: s.color, lineHeight: 1 }}>{s.value}</div>
                                            <div style={{ fontSize: '.7rem', color: '#94a3b8', fontWeight: '600', marginTop: '2px' }}>{s.label}</div>
                                        </div>
                                        {i < 2 && <div style={{ width: '1px', background: '#f1f5f9', alignSelf: 'stretch' }} />}
                                    </React.Fragment>
                                ))}
                            </div>
                            <div style={{ display: 'flex', gap: '5px', marginBottom: '28px' }}>
                                {[colors.primary, colors.secondary, colors.accentDark, colors.secondaryLight].map((c, i) => (
                                    <div key={i} style={{ height: '3px', width: i === 0 ? '28px' : '10px', borderRadius: '3px', background: c }} />
                                ))}
                            </div>
                            <a href="/contacto" style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark})`,
                                color: '#fff', borderRadius: '13px', padding: '13px 28px',
                                fontWeight: '800', fontSize: '.95rem', textDecoration: 'none',
                                boxShadow: `0 6px 20px ${colors.primary}35`, transition: 'all .22s',
                            }}
                                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 10px 28px ${colors.primary}45`; }}
                                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = `0 6px 20px ${colors.primary}35`; }}
                            >
                                Unirme a la comunidad <ArrowRight size={15} />
                            </a>
                        </div>

                        {/* RIGHT — grid de logos (primeras 9 empresas) */}
                        <div className="nc-hero-visual" style={{
                            flex: 1, maxWidth: '480px',
                            opacity: heroVisible ? 1 : 0,
                            transform: heroVisible ? 'translateX(0)' : 'translateX(40px)',
                            transition: 'opacity 1s ease .15s, transform 1s cubic-bezier(.16,1,.3,1) .15s',
                        }}>
                            {loading ? (
                                <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Cargando...</div>
                            ) : error ? (
                                <div style={{ textAlign: 'center', color: '#ef4444' }}>Error al cargar</div>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px' }}>
                                    {heroClients.map((c, i) => (
                                        <div key={c.name} style={{
                                            background: '#fff', borderRadius: '16px', padding: '18px 12px',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                                            border: '1.5px solid #f1f5f9',
                                            boxShadow: '0 2px 12px rgba(0,0,0,.04)',
                                            transition: 'all .2s',
                                        }}
                                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = `${c.color}30`; }}
                                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = '#f1f5f9'; }}
                                        >
                                            <div style={{
                                                width: '40px', height: '40px', borderRadius: '12px',
                                                background: `linear-gradient(135deg, ${c.color}22, ${c.color}10)`,
                                                border: `1.5px solid ${c.color}20`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontWeight: '800', fontSize: '.85rem', color: c.color,
                                            }}>{c.initial}</div>
                                            <div style={{ fontSize: '.68rem', fontWeight: '700', color: '#0f172a', textAlign: 'center', lineHeight: 1.2 }}>{c.name}</div>
                                            <div style={{ fontSize: '.6rem', color: '#94a3b8', fontWeight: '500' }}>{c.category}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* ══ STATS ══ */}
            <section className="nc-section" style={{ background: '#fff', padding: '72px clamp(20px,4vw,40px) 60px' }}>
                <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
                    <div className="nc-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '16px' }}>
                        {STATS.map(s => (
                            <div key={s.label} style={{
                                background: '#fff', borderRadius: '20px', padding: '28px 20px',
                                border: '1.5px solid #f1f5f9', textAlign: 'center',
                                boxShadow: '0 2px 12px rgba(0,0,0,.04)',
                                borderTop: `4px solid ${s.color}`,
                                transition: 'all .25s',
                            }}
                                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
                                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
                            >
                                <div style={{ width: '44px', height: '44px', borderRadius: '12px', background: `${s.color}12`, color: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                                    {s.icon}
                                </div>
                                <div style={{ fontSize: '2rem', fontWeight: '900', color: '#0f172a', letterSpacing: '-.03em', lineHeight: 1 }}>{s.value}</div>
                                <div style={{ fontSize: '.78rem', color: '#64748b', fontWeight: '600', marginTop: '6px' }}>{s.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══ GRID DE CLIENTES (dinámico desde API) ══ */}
            <section className="nc-section" style={{ background: '#fff', padding: '20px clamp(20px,4vw,40px) 80px' }}>
                <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                            <div style={{ height: '2px', width: '36px', background: colors.primary, borderRadius: '2px' }} />
                            <span style={{ fontSize: '.75rem', fontWeight: '700', letterSpacing: '.14em', textTransform: 'uppercase', color: colors.primary }}>
                                Directorio completo
                            </span>
                            <div style={{ height: '2px', width: '36px', background: colors.primary, borderRadius: '2px' }} />
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.8rem,3vw,2.6rem)', fontWeight: '900', color: '#0f172a', letterSpacing: '-.02em' }}>
                            Empresas que{' '}
                            <span style={{ color: colors.primary }}>ya trabajan con nosotros</span>
                        </h2>
                    </div>
                    {renderClientesGrid()}
                </div>
            </section>

            {/* ══ CÓMO TRABAJAMOS ══ */}
            <section className="nc-section" style={{ background: '#fff', padding: '0 clamp(20px,4vw,40px) 80px' }}>
                <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '52px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                            <div style={{ height: '2px', width: '36px', background: colors.secondary, borderRadius: '2px' }} />
                            <span style={{ fontSize: '.75rem', fontWeight: '700', letterSpacing: '.14em', textTransform: 'uppercase', color: colors.secondary }}>
                                Nuestro enfoque
                            </span>
                            <div style={{ height: '2px', width: '36px', background: colors.secondary, borderRadius: '2px' }} />
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.3rem)', fontWeight: '900', color: '#0f172a', letterSpacing: '-.02em' }}>
                            Cómo acompañamos a{' '}
                            <span style={{ color: colors.secondary }}>cada cliente</span>
                        </h2>
                        <p style={{ fontSize: '.95rem', color: '#64748b', marginTop: '10px', maxWidth: '480px', margin: '10px auto 0' }}>
                            Desde el primer día hasta el largo plazo, nuestro modelo está diseñado para adaptarse y crecer con vos.
                        </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '20px' }}>
                        {[
                            {
                                step: '01',
                                title: 'Diagnóstico personalizado',
                                desc: 'Antes de asignarte un espacio, nuestro equipo entiende tu dinámica de trabajo, tamaño de equipo, frecuencia de uso y objetivos de negocio. Sin plantillas genéricas.',
                                color: colors.secondary,

                            },
                            {
                                step: '02',
                                title: 'Espacio a tu medida',
                                desc: 'Diseñamos una solución que combina el tipo de espacio, la membresía y los servicios exactos que necesitás — ya sea un escritorio flex, una oficina privada o un piso completo.',
                                color: colors.primary,

                            },
                            {
                                step: '03',
                                title: 'Acompañamiento continuo',
                                desc: 'Un account manager dedicado hace seguimiento mensual de tu experiencia, gestiona solicitudes y ajusta el plan conforme crece tu empresa. Tu éxito es nuestro éxito.',
                                color: colors.accentDark,

                            },
                        ].map((item, i) => (
                            <div key={item.step} style={{
                                background: '#fff', borderRadius: '22px', padding: '32px 26px',
                                border: '1.5px solid #f1f5f9',
                                boxShadow: '0 2px 16px rgba(0,0,0,.04)',
                                borderTop: `4px solid ${item.color}`,
                                transition: 'all .25s cubic-bezier(.16,1,.3,1)',
                                position: 'relative',
                            }}
                                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = `0 16px 40px ${item.color}14`; }}
                                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 16px rgba(0,0,0,.04)'; }}
                            >
                                <div style={{
                                    position: 'absolute', top: '16px', right: '20px',
                                    fontSize: '3.5rem', fontWeight: '900', color: `${item.color}08`,
                                    lineHeight: 1, letterSpacing: '-.04em', userSelect: 'none',
                                }}>{item.step}</div>

                                <div style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                                    background: `${item.color}10`, borderRadius: '100px',
                                    padding: '3px 12px', marginBottom: '12px',
                                }}>
                                    <span style={{ fontSize: '.65rem', fontWeight: '800', color: item.color, letterSpacing: '.08em', textTransform: 'uppercase' }}>
                                        Paso {item.step}
                                    </span>
                                </div>
                                <h3 style={{ fontSize: '1.05rem', fontWeight: '800', color: '#0f172a', letterSpacing: '-.01em', marginBottom: '10px', lineHeight: 1.25 }}>
                                    {item.title}
                                </h3>
                                <p style={{ fontSize: '.82rem', color: '#64748b', lineHeight: 1.7 }}>
                                    {item.desc}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══ TESTIMONIOS (estáticos) ══ */}
            <section className="nc-section" style={{ background: '#fff', padding: '20px clamp(20px,4vw,40px) 80px' }}>
                <div style={{ maxWidth: '1180px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '48px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                            <div style={{ height: '2px', width: '36px', background: colors.accentDark, borderRadius: '2px' }} />
                            <span style={{ fontSize: '.75rem', fontWeight: '700', letterSpacing: '.14em', textTransform: 'uppercase', color: colors.accentDark }}>
                                Lo que dicen
                            </span>
                            <div style={{ height: '2px', width: '36px', background: colors.accentDark, borderRadius: '2px' }} />
                        </div>
                        <h2 style={{ fontSize: 'clamp(1.8rem,3vw,2.6rem)', fontWeight: '900', color: '#0f172a', letterSpacing: '-.02em' }}>
                            La voz de nuestros{' '}
                            <span style={{ color: colors.accentDark }}>clientes</span>
                        </h2>
                    </div>

                    <div className="nc-testimonials-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '20px' }}>
                        {TESTIMONIALS.map(t => (
                            <div key={t.name} className="nc-testimonial-card" style={{
                                background: '#fff', borderRadius: '20px', padding: '28px 24px',
                                border: '1.5px solid #f1f5f9',
                                boxShadow: '0 2px 16px rgba(0,0,0,.05)',
                                borderTop: `4px solid ${t.color}`,
                                display: 'flex', flexDirection: 'column', gap: '16px',
                            }}>
                                <Quote size={20} color={`${t.color}40`} style={{ flexShrink: 0 }} />
                                <p style={{ fontSize: '.87rem', color: '#475569', lineHeight: 1.75, fontStyle: 'italic', flex: 1 }}>
                                    "{t.text}"
                                </p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', paddingTop: '12px', borderTop: '1.5px solid #f1f5f9' }}>
                                    <div style={{
                                        width: '40px', height: '40px', borderRadius: '50%',
                                        background: `linear-gradient(135deg, ${t.color}, ${t.color}88)`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        color: '#fff', fontWeight: '800', fontSize: '.78rem', flexShrink: 0,
                                    }}>{t.avatar}</div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: '800', fontSize: '.85rem', color: '#0f172a' }}>{t.name}</div>
                                        <div style={{ fontSize: '.7rem', color: '#94a3b8', fontWeight: '500' }}>{t.role} · {t.company}</div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '1px' }}>
                                        {[...Array(t.stars)].map((_, i) => <Star key={i} size={10} color={colors.accentDark} fill={colors.accentDark} />)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══ CTA FINAL (Tarjeta Blanca con BorderGlow) ══ */}
            <section
                className="nc-section"
                style={{
                    background: isDark ? '#0b0614' : '#f8fafc',
                    padding: '40px clamp(20px,4vw,40px) 90px',
                    transition: 'background-color 0.3s ease',
                }}
            >
                <div style={{ maxWidth: '920px', margin: '0 auto' }}>
                    <BorderGlow
                        borderRadius={28}
                        backgroundColor={isDark ? '#150d24' : '#ffffff'}
                    >
                        <div
                            style={{
                                background: isDark ? '#150d24' : '#ffffff',
                                borderRadius: '28px',
                                padding: '64px clamp(24px, 5vw, 60px)',
                                textAlign: 'center',
                                position: 'relative',
                                overflow: 'hidden',
                                border: isDark ? '1px solid rgba(193, 52, 139, 0.22)' : '1.5px solid #f1f5f9',
                                boxShadow: isDark
                                    ? '0 24px 60px rgba(0,0,0,0.5)'
                                    : '0 20px 48px rgba(97, 37, 123, 0.08), 0 4px 16px rgba(0,0,0,0.02)',
                                transition: 'all 0.3s ease',
                            }}
                        >
                            {/* Halos decorativos suaves de fondo */}
                            <div
                                style={{
                                    position: 'absolute',
                                    top: '-80px',
                                    right: '-80px',
                                    width: '260px',
                                    height: '260px',
                                    borderRadius: '50%',
                                    background: isDark
                                        ? 'radial-gradient(circle, rgba(193, 52, 139, 0.12) 0%, transparent 70%)'
                                        : 'radial-gradient(circle, rgba(193, 52, 139, 0.06) 0%, transparent 70%)',
                                    pointerEvents: 'none',
                                }}
                            />
                            <div
                                style={{
                                    position: 'absolute',
                                    bottom: '-70px',
                                    left: '-70px',
                                    width: '240px',
                                    height: '240px',
                                    borderRadius: '50%',
                                    background: isDark
                                        ? 'radial-gradient(circle, rgba(97, 37, 123, 0.14) 0%, transparent 70%)'
                                        : 'radial-gradient(circle, rgba(97, 37, 123, 0.05) 0%, transparent 70%)',
                                    pointerEvents: 'none',
                                }}
                            />

                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <h2
                                    style={{
                                        fontSize: 'clamp(1.85rem, 3.2vw, 2.6rem)',
                                        fontWeight: '900',
                                        color: isDark ? '#ffffff' : '#0f172a',
                                        letterSpacing: '-.025em',
                                        marginBottom: '16px',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    ¿Tu empresa aún no es parte?
                                </h2>
                                <p
                                    style={{
                                        fontSize: '1.05rem',
                                        color: isDark ? '#cbd5e1' : '#64748b',
                                        lineHeight: 1.75,
                                        marginBottom: '36px',
                                        maxWidth: '520px',
                                        margin: '0 auto 36px',
                                        fontWeight: '400',
                                    }}
                                >
                                    Únete a más de 300 empresas que ya encontraron en Co Marca el espacio ideal para crecer, conectar e innovar.
                                </p>
                                <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <a
                                        href="/contacto"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            background: 'linear-gradient(135deg, #61257B 0%, #C1348B 100%)',
                                            color: '#ffffff',
                                            borderRadius: '14px',
                                            padding: '14px 30px',
                                            fontWeight: '800',
                                            fontSize: '1rem',
                                            textDecoration: 'none',
                                            boxShadow: '0 8px 24px rgba(97, 37, 123, 0.28)',
                                            transition: 'all .22s cubic-bezier(.16,1,.3,1)',
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 12px 30px rgba(97, 37, 123, 0.38)';
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 8px 24px rgba(97, 37, 123, 0.28)';
                                        }}
                                    >
                                        Hablar con el equipo →
                                    </a>
                                    <a
                                        href="/reservaya"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            background: isDark ? 'rgba(255,255,255,0.06)' : '#ffffff',
                                            border: isDark ? '1.5px solid rgba(193, 52, 139, 0.4)' : '1.5px solid rgba(97, 37, 123, 0.25)',
                                            color: isDark ? '#ffffff' : colors.secondary,
                                            borderRadius: '14px',
                                            padding: '14px 26px',
                                            fontWeight: '700',
                                            fontSize: '.98rem',
                                            textDecoration: 'none',
                                            boxShadow: isDark ? 'none' : '0 2px 10px rgba(0,0,0,.03)',
                                            transition: 'all .22s cubic-bezier(.16,1,.3,1)',
                                        }}
                                        onMouseEnter={e => {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.12)' : 'rgba(97, 37, 123, 0.05)';
                                            e.currentTarget.style.borderColor = colors.primary;
                                        }}
                                        onMouseLeave={e => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.06)' : '#ffffff';
                                            e.currentTarget.style.borderColor = isDark ? 'rgba(193, 52, 139, 0.4)' : 'rgba(97, 37, 123, 0.25)';
                                        }}
                                    >
                                        Reservar un espacio
                                    </a>
                                </div>
                                <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginTop: '36px' }}>
                                    {[colors.secondary, colors.primary, colors.accentDark, colors.accent].map((c, i) => (
                                        <div
                                            key={i}
                                            style={{
                                                height: '4px',
                                                width: i === 0 ? '30px' : '10px',
                                                borderRadius: '4px',
                                                background: c,
                                                opacity: 0.85,
                                            }}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </BorderGlow>
                </div>
            </section>
        </div>
    );
};

export default NuestrosClientes;